import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, CaretDoubleLeft, CaretDoubleRight } from "@phosphor-icons/react";

/**
 * Pagination controls.
 * - page: 1-based current page number
 * - pageSize: rows per page
 * - total: total row count
 * - onPageChange(nextPage)
 * - onPageSizeChange(nextSize)
 */
export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange, testId = "pagination" }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);

  const go = (p) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    if (clamped !== page) onPageChange?.(clamped);
  };

  return (
    <div data-testid={testId} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t">
      <div className="text-xs text-muted-foreground mono">
        Showing <span className="font-medium text-foreground">{start}–{end}</span> of <span className="font-medium text-foreground">{total}</span>
      </div>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Rows per page</span>
            <select
              data-testid={`${testId}-size`}
              value={pageSize}
              onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
              className="h-8 px-2 rounded-md border bg-background text-sm"
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Button data-testid={`${testId}-first`} variant="outline" size="icon" className="h-8 w-8" onClick={() => go(1)} disabled={page <= 1}>
            <CaretDoubleLeft size={14} />
          </Button>
          <Button data-testid={`${testId}-prev`} variant="outline" size="icon" className="h-8 w-8" onClick={() => go(page - 1)} disabled={page <= 1}>
            <CaretLeft size={14} />
          </Button>
          <span data-testid={`${testId}-status`} className="mono text-xs px-3">Page {page} of {totalPages}</span>
          <Button data-testid={`${testId}-next`} variant="outline" size="icon" className="h-8 w-8" onClick={() => go(page + 1)} disabled={page >= totalPages}>
            <CaretRight size={14} />
          </Button>
          <Button data-testid={`${testId}-last`} variant="outline" size="icon" className="h-8 w-8" onClick={() => go(totalPages)} disabled={page >= totalPages}>
            <CaretDoubleRight size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
